import { useEffect, useState } from "react";
import { Loader2, Receipt, Save } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Lets dealership managers set a separate billing email + VAT/tax ID for accounting.
 * Used by the Paddle checkout so invoices route correctly.
 */
export function BillingDetailsCard() {
  const { userId, mode, isAuthenticated } = useAuth();
  const [billingEmail, setBillingEmail] = useState("");
  const [taxId, setTaxId] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || mode !== "real" || !userId) { setLoading(false); return; }
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("billing_email, tax_id, tax_id_country")
        .eq("id", userId)
        .maybeSingle();
      setBillingEmail((data as any)?.billing_email ?? "");
      setTaxId((data as any)?.tax_id ?? "");
      setCountry((data as any)?.tax_id_country ?? "");
      setLoading(false);
    })();
  }, [userId, mode, isAuthenticated]);

  if (mode !== "real") return null;

  const save = async () => {
    if (!userId) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        billing_email: billingEmail.trim() || null,
        tax_id: taxId.trim() || null,
        tax_id_country: country.trim().toUpperCase().slice(0, 2) || null,
      } as any)
      .eq("id", userId);
    setSaving(false);
    if (error) { toast.error("Couldn't save", { description: error.message }); return; }
    toast.success("Billing details saved");
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary" /> Invoice details
        </CardTitle>
        <CardDescription>
          Send invoices to accounting and add a VAT/tax ID. Used at your next checkout or plan change.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="billing-email" className="text-xs">Billing email (optional)</Label>
            <Input
              id="billing-email"
              type="email"
              placeholder="accounting@dealership.com"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tax-id" className="text-xs">Tax / VAT ID (optional)</Label>
            <Input
              id="tax-id"
              placeholder="e.g. GB123456789"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tax-country" className="text-xs">Country (ISO-2)</Label>
            <Input
              id="tax-country"
              placeholder="CA"
              maxLength={2}
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              className="h-9 uppercase"
            />
          </div>
        </div>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save details
        </Button>
      </CardContent>
    </Card>
  );
}